import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { useGlobalModalContext } from '../../../../../context/GlobalModal';
import { Button, Modal, Splash } from '../../../../../shared/Components';

const ModalTitle = styled.div`
  width: 100%;
  text-align: center;
  font-size: 20px;
  font-weight: 600;
`;

const ModalActions = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-evenly;
`;

interface ConfirmationModalProps {
  action?(): boolean;
  onCancel?(): boolean;
  title?: string;
  content?(): void;
  disabled?: boolean;
  confirmText?: string;
  disableNotifications?: boolean;
}

enum ConfirmationModalState {
  NONE,
  LOADING,
  SUCCESS,
  FAILED,
}

export default function ConfirmationModal({
  action,
  onCancel,
  title,
  content,
  disabled,
  confirmText,
  disableNotifications,
}: ConfirmationModalProps) {
  const { hideModal } = useGlobalModalContext();
  const [state, setState] = useState<ConfirmationModalState>(ConfirmationModalState.NONE);

  const confirm = async () => {
    setState(ConfirmationModalState.LOADING);
    try {
      const actionRes: boolean = action ? await action() : true;
      if (actionRes) setState(ConfirmationModalState.SUCCESS);
      setState(ConfirmationModalState.FAILED);
    } catch (err) {
      setState(ConfirmationModalState.FAILED);
    }
  };

  const cancel = useCallback(() => {
    hideModal();
    onCancel && onCancel();
  }, []);

  useEffect(() => {
    switch (state) {
      case ConfirmationModalState.SUCCESS:
        hideModal();
        if (!disableNotifications) toast.success('Action confirmed');
        break;

      case ConfirmationModalState.FAILED:
        if (!disableNotifications) toast.error('Failed to execute action');
        break;
    }
  }, [state, disableNotifications]);

  return (
    <Modal onRequestClose={cancel}>
      <ModalTitle>{title}</ModalTitle>
      {content && content()}
      <ModalActions>
        {state != ConfirmationModalState.LOADING && (
          <>
            <Button onClick={confirm} width={100} fontSize={16} height={26} disabled={disabled}>
              {confirmText ?? 'Confirm'}
            </Button>
            <Button variant="danger" onClick={cancel} width={100} fontSize={16} height={26}>
              Cancel
            </Button>
          </>
        )}
        {state == ConfirmationModalState.LOADING && <Splash position="relative" />}
      </ModalActions>
    </Modal>
  );
}
